/**
 * useBookAnimationTracking Hook
 * 
 * Tracks which books have been animated (typewriter effect shown) in current session.
 * Session-based tracking means animation shows once per app session, not persisted.
 * 
 * Usage:
 * - Wrap in Context Provider at HomePage level
 * - Consume in BookPersonalizationMessage component
 * - Mark books as animated after typewriter effect completes
 */

import { useState, useCallback } from 'react';

export interface BookAnimationTrackingState {
  hasAnimated: (bookId: string) => boolean;
  markAsAnimated: (bookId: string) => void;
  reset: () => void;
}

/**
 * Hook to track which books have had their animation played in this session
 */
export function useBookAnimationTracking(): BookAnimationTrackingState {
  const [animatedBooks, setAnimatedBooks] = useState<Set<string>>(new Set());
  
  const hasAnimated = useCallback((bookId: string): boolean => {
    return animatedBooks.has(bookId);
  }, [animatedBooks]);
  
  const markAsAnimated = useCallback((bookId: string): void => {
    setAnimatedBooks(prev => {
      const next = new Set(prev);
      next.add(bookId);
      return next;
    });
  }, []);
  
  const reset = useCallback((): void => {
    setAnimatedBooks(new Set());
  }, []);
  
  return { hasAnimated, markAsAnimated, reset };
}

